import { getGenericBounds } from '../../lib/bound';
import {
  addElement,
  defaultGenericElement,
  type GenericElement,
  reversionElement,
  updateElement,
} from '../../lib/element';
import { prepareElementForExport } from '../../lib/exportImport';
import { type Plugin } from '../../lib/plugin';
import { renderStaticScene } from '../../lib/scene';
import { updateRuntimeStore } from '../../stores/useEditorRuntimeStore';
import { updateEditorStore, useEditorStore } from '../../stores/useEditorStore';
import { ensureLoadedImage, shrinkImageDimensions } from '../../utils/image';
import { general } from '../general';

import { getImageShape } from './utils/shape';

export interface ImageElement extends GenericElement {
  image: HTMLImageElement;
  type: 'image';
  src?: string;
}

const imageActions = {
  addCenterImage: (
    image: HTMLImageElement,
    options: {
      id?: string;
      src?: string;
      scale?: number;
      isVisible?: boolean;
    } = {},
  ) => {
    const imageWidth = image.naturalWidth;
    const imageHeight = image.naturalHeight;

    const defaults = defaultGenericElement();

    const element: ImageElement = {
      ...defaults,
      id: options.id || defaults.id,
      height: imageHeight,
      image: image,
      isLocked: true,
      type: 'image',
      layer: 0,
      width: imageWidth,
      x: 0,
      y: 0,
      isVisible: options.isVisible ?? defaults.isVisible,
      src: options?.src,
    };

    addElement(useEditorStore.getState().elements, element);

    general.actions.zoomToFitElement(element);

    if (options.isVisible ?? true) {
      renderStaticScene();
    }

    return element;
  },
  replaceCenterImage: async (src: string) => {
    const imageElement = Array.from(
      useEditorStore.getState().elements.values(),
    ).find(
      (element): element is ImageElement =>
        element.type === 'image' && element.layer === 0,
    );

    if (!imageElement) return;

    let image: HTMLImageElement;
    try {
      image = await ensureLoadedImage(src, 'anonymous');
    } catch {
      return;
    }

    updateElement(
      useEditorStore.getState().elements,
      imageElement,
      {
        src,
        image,
      },
      true,
    );

    return imageElement;
  },
  uploadAsNewElement: (
    image: HTMLImageElement,
    options?: Partial<ImageElement>,
  ) => {
    const [previewWidth, previewHeight] = shrinkImageDimensions(
      [image.width, image.height],
      [window.innerWidth * 0.1, window.innerHeight * 0.1],
    );

    general.actions.activate('image');

    updateRuntimeStore({
      newElement: {
        ...defaultGenericElement(),
        height: previewHeight,
        image: image,
        type: 'image',
        width: previewWidth,
        ...options,
      } as GenericElement,
    });
  },
};

const plugin: Plugin<ImageElement, typeof imageActions> = {
  name: 'image',
  cursor: 'default',
  actions: imageActions,
  events: {
    onPointerMove: ({ event, runtimeState, helpers }) => {
      if (runtimeState.activeTool !== 'image' || !runtimeState.newElement)
        return;

      const { pos } = event;

      updateRuntimeStore({
        newElement: {
          ...runtimeState.newElement,
          x: pos.x - runtimeState.newElement.width / 2,
          y: pos.y - runtimeState.newElement.height / 2,
          ...reversionElement(runtimeState.newElement, ['bounds']),
        },
      });

      helpers.renderNewElementScene();
      helpers.showNewElementCanvas();
    },
    onPointerUp: ({ elementState, helpers, history, runtimeState }) => {
      if (!runtimeState.newElement || runtimeState.activeTool !== 'image')
        return;

      const newElement = runtimeState.newElement as ImageElement;

      const [newWidth, newHeight] = shrinkImageDimensions(
        [newElement.image.width, newElement.image.height],
        [window.innerWidth * 0.7, window.innerHeight * 0.7],
      );

      const imageElement = {
        ...newElement,
        height: newHeight,
        shape: getImageShape(newElement),
        width: newWidth,
        x: newElement.x + newElement.width / 2 - newWidth / 2,
        y: newElement.y + newElement.height / 2 - newHeight / 2,
      };

      addElement(elementState.elements, imageElement);
      history.push(
        updateEditorStore({ selectedElementIds: [imageElement.id] }),
      );
      updateRuntimeStore({ activeTool: 'selection', newElement: undefined });

      helpers.hideNewElementCanvas();
      helpers.renderStaticScene();
      helpers.renderInteractiveScene();
    },
  },
  render: {
    draw: ({ config, context, element }) => {
      const radius = 4;

      const { height, width } = element;

      context.beginPath();
      context.moveTo(radius, 0);
      context.arcTo(width, 0, width, height, radius); // top-right corner
      context.arcTo(width, height, 0, height, radius); // bottom-right corner
      context.arcTo(0, height, 0, 0, radius); // bottom-left corner
      context.arcTo(0, 0, width, 0, radius); // top-left corner
      context.clip();

      if (element.opacity) {
        context.globalAlpha = element.opacity;
      }

      if (element.isDeletedFlag) {
        context.globalAlpha = config.canvas.deletingElementOpacity;
      }

      context.drawImage(element.image, 0, 0, element.width, element.height);
    },
  },
  collision: {
    bounds: (element) => getGenericBounds(element),
    calculateShape: (element) => getImageShape(element),
    isPointInShape: () => false,
    isPointOnShape: () => false,
  },
  transformation: {
    resizedBounds: (element, nextWidth, nextHeight) => [
      element.x,
      element.y,
      element.x + nextWidth,
      element.y + nextHeight,
      0,
      0,
    ],
    shiftBySizeOnFlip: true,
  },
  export: (element) => {
    if (!element.src) return;
    const prepared = prepareElementForExport(element) as ImageElement;

    return { ...prepared, src: element.src, image: undefined };
  },
  import: async (element) => {
    if (!element.src) return;

    const imported = {
      ...defaultGenericElement(),
      ...element,
      layer: 0,
    } as ImageElement;

    imported.shape = getImageShape(imported);

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = element.src;

    await new Promise((resolve) => {
      img.onload = resolve;
      img.onerror = resolve;
    });

    imported.image = img;

    return imported;
  },
};

export const image = plugin;
